import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { posAPI } from '@/services/api';
import { RotateCcw, Minus, Plus, IndianRupee, Banknote, CreditCard, Wallet, CheckCircle, Printer, AlertCircle } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';

const ReturnRefundDialog = ({ open, onOpenChange, bill, onSuccess }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1); // 1: Select Items, 2: Refund Payment
  const [returnItems, setReturnItems] = useState([]);
  const [refundMode, setRefundMode] = useState('cash');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [returnComplete, setReturnComplete] = useState(false);
  const [returnResult, setReturnResult] = useState(null);
  const receiptRef = useRef(null);

  // Initialize return items from bill
  useEffect(() => {
    if (bill && open) {
      const items = bill.items?.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        sku: item.sku,
        quantity: item.quantity,
        returned_qty: item.returned_qty || 0,
        available_qty: item.quantity - (item.returned_qty || 0),
        return_qty: 0,
        unit_price: item.unit_price,
        tax_rate: item.tax_rate || 0,
        reason: 'customer_request',
        condition: 'good'
      })) || [];
      setReturnItems(items);
      setStep(1);
      setReturnComplete(false);
      setReturnResult(null);
      setNotes('');
      setRefundMode('cash');
    }
  }, [bill, open]);

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let tax = 0;
    
    returnItems.forEach(item => {
      if (item.return_qty > 0) {
        const lineTotal = item.return_qty * item.unit_price;
        subtotal += lineTotal;
        // GST-inclusive: extract tax from total
        if (item.tax_rate > 0) {
          const taxable = lineTotal / (1 + item.tax_rate / 100);
          tax += lineTotal - taxable;
        }
      }
    });
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round(subtotal * 100) / 100 // Already GST inclusive
    };
  };

  const totals = calculateTotals();

  // Update return quantity
  const updateReturnQty = (index, qty) => {
    const item = returnItems[index];
    const newQty = Math.max(0, Math.min(qty, item.available_qty));
    setReturnItems(prev => prev.map((it, i) => 
      i === index ? { ...it, return_qty: newQty } : it
    ));
  };

  // Update reason
  const updateReason = (index, reason) => {
    setReturnItems(prev => prev.map((it, i) => 
      i === index ? { ...it, reason } : it
    ));
  };

  // Update condition
  const updateCondition = (index, condition) => {
    setReturnItems(prev => prev.map((it, i) => 
      i === index ? { ...it, condition } : it
    ));
  };

  // Proceed to payment step
  const proceedToPayment = () => {
    const itemsToReturn = returnItems.filter(it => it.return_qty > 0);
    if (itemsToReturn.length === 0) {
      toast({ title: 'Error', description: 'Select at least one item to return', variant: 'destructive' });
      return;
    }
    setStep(2);
  };

  // Process the return
  const processReturn = async () => {
    setProcessing(true);
    try {
      const itemsToReturn = returnItems
        .filter(it => it.return_qty > 0)
        .map(it => ({
          product_id: it.product_id,
          quantity: it.return_qty,
          reason: it.reason,
          condition: it.condition
        }));

      const res = await posAPI.createReturn({
        sale_id: bill.id,
        items: itemsToReturn,
        refund_mode: refundMode,
        notes: notes
      });

      setReturnResult(res.data);
      setReturnComplete(true);
      toast({ title: 'Success', description: 'Return processed successfully' });
      
    } catch (error) {
      console.error('Return failed:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to process return', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  // Print return receipt
  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Return-${returnResult?.return_number || 'Receipt'}`,
  });

  // Close and refresh
  const handleClose = () => {
    if (returnComplete && onSuccess) {
      onSuccess(returnResult);
    }
    onOpenChange(false);
  };

  const hasSelectedItems = returnItems.some(it => it.return_qty > 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-800">
            <RotateCcw className="w-5 h-5 text-orange-500" />
            {returnComplete ? 'Return Complete' : step === 1 ? 'Process Return' : 'Refund Payment'}
          </DialogTitle>
        </DialogHeader>

        {!returnComplete ? (
          <>
            {/* Bill Reference */}
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Original Bill</p>
                    <p className="font-bold text-gray-800">{bill?.bill_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Bill Amount</p>
                    <p className="font-bold text-gray-800">₹{bill?.grand_total?.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {step === 1 && (
              <>
                {/* Items Selection */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-center">Purchased</TableHead>
                        <TableHead className="text-center">Available</TableHead>
                        <TableHead className="text-center">Return Qty</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Refund</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnItems.map((item, idx) => (
                        <TableRow key={idx} className={item.return_qty > 0 ? 'bg-orange-50' : ''}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.available_qty > 0 ? 'default' : 'secondary'}>
                              {item.available_qty}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateReturnQty(idx, item.return_qty - 1)}
                                disabled={item.return_qty <= 0}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <Input
                                type="number"
                                value={item.return_qty}
                                onChange={(e) => updateReturnQty(idx, parseInt(e.target.value) || 0)}
                                className="w-14 h-7 text-center"
                                min={0}
                                max={item.available_qty}
                              />
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => updateReturnQty(idx, item.return_qty + 1)}
                                disabled={item.return_qty >= item.available_qty}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={item.reason} 
                              onValueChange={(v) => updateReason(idx, v)}
                              disabled={item.return_qty <= 0}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="customer_request">Customer Request</SelectItem>
                                <SelectItem value="defective">Defective</SelectItem>
                                <SelectItem value="wrong_item">Wrong Item</SelectItem>
                                <SelectItem value="damaged">Damaged</SelectItem>
                                <SelectItem value="expired">Expired</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-orange-600">
                            {item.return_qty > 0 ? `₹${(item.return_qty * item.unit_price).toFixed(2)}` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes (Required)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Reason for return, customer details, etc."
                    className="bg-gray-50"
                    rows={2}
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                {/* Refund Summary */}
                <Card className="bg-gray-50">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="font-semibold text-gray-800 border-b pb-2">Refund Summary</h3>
                    
                    {returnItems.filter(it => it.return_qty > 0).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.product_name} x {item.return_qty}</span>
                        <span>₹{(item.return_qty * item.unit_price).toFixed(2)}</span>
                      </div>
                    ))}
                    
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal (incl. GST)</span>
                        <span>₹{totals.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Tax (included)</span>
                        <span>₹{totals.tax.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="border-t pt-3 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-800">REFUND AMOUNT</span>
                        <span className="text-2xl font-bold text-orange-600">₹{totals.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Refund Mode Selection */}
                <div className="space-y-3">
                  <Label className="text-gray-700">Select Refund Mode</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Button
                      type="button"
                      variant={refundMode === 'cash' ? 'default' : 'outline'}
                      onClick={() => setRefundMode('cash')}
                      className={`h-20 flex-col gap-2 ${refundMode === 'cash' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    >
                      <Banknote className="w-6 h-6" />
                      <span>Cash Refund</span>
                    </Button>
                    <Button
                      type="button"
                      variant={refundMode === 'original' ? 'default' : 'outline'}
                      onClick={() => setRefundMode('original')}
                      className={`h-20 flex-col gap-2 ${refundMode === 'original' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    >
                      <CreditCard className="w-6 h-6" />
                      <span>Original Method</span>
                    </Button>
                    <Button
                      type="button"
                      variant={refundMode === 'store_credit' ? 'default' : 'outline'}
                      onClick={() => setRefundMode('store_credit')}
                      className={`h-20 flex-col gap-2 ${refundMode === 'store_credit' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    >
                      <Wallet className="w-6 h-6" />
                      <span>Store Credit</span>
                    </Button>
                  </div>
                </div>

                {/* Notes Display */}
                {notes && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800"><strong>Notes:</strong> {notes}</p>
                  </div>
                )}

                {/* Warning for Cash Refund */}
                {refundMode === 'cash' && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold">Cash Refund</p>
                      <p>This amount will be deducted from your cash register on closing.</p>
                    </div>
                  </div>
                )}
              </>
            )}

            <DialogFooter className="gap-2">
              {step === 2 && (
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
              )}
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {step === 1 ? (
                <Button
                  onClick={proceedToPayment}
                  disabled={!hasSelectedItems || !notes.trim()}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Proceed to Refund →
                </Button>
              ) : (
                <Button
                  onClick={processReturn}
                  disabled={processing}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {processing ? 'Processing...' : `Refund ₹${totals.total.toFixed(2)}`}
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Return Complete */}
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">Return Processed Successfully</h2>
              <p className="text-gray-600 mb-4">Return #{returnResult?.return_number}</p>
              
              <Card className="bg-green-50 border-green-200 mb-4">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-green-600">
                    ₹{returnResult?.total_refund?.toFixed(2)} Refunded
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    via {refundMode === 'cash' ? 'Cash' : refundMode === 'original' ? 'Original Payment Method' : 'Store Credit'}
                  </p>
                </CardContent>
              </Card>

              {/* Return Receipt - Thermal Printer Optimized */}
              <div className="hidden">
                <div ref={receiptRef} className="return-receipt-thermal">
                  {/* Header */}
                  <div className="receipt-header">
                    <div className="receipt-title">RETURN RECEIPT</div>
                    <div className="store-name">{bill?.store_name || 'SOON YOUNG'}</div>
                  </div>

                  {/* Receipt Info */}
                  <div className="info-section">
                    <div className="info-row">
                      <span>Return #:</span>
                      <span className="bold">{returnResult?.return_number}</span>
                    </div>
                    <div className="info-row">
                      <span>Original Bill:</span>
                      <span>{bill?.bill_number}</span>
                    </div>
                    <div className="info-row">
                      <span>Date:</span>
                      <span>{new Date().toLocaleDateString('en-IN')} {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>

                  {/* Returned Items */}
                  <div className="items-section">
                    <div className="section-title">RETURNED ITEMS</div>
                    <div className="items-header">
                      <span className="col-item">ITEM</span>
                      <span className="col-qty">QTY</span>
                      <span className="col-amt">AMT</span>
                    </div>
                    {returnItems.filter(it => it.return_qty > 0).map((item, idx) => (
                      <div key={idx} className="item-row">
                        <div className="item-name">{item.product_name}</div>
                        <div className="item-details">
                          <span className="col-qty">{item.return_qty}</span>
                          <span className="col-rate">@ Rs.{item.unit_price.toFixed(0)}</span>
                          <span className="col-amt">Rs.{(item.return_qty * item.unit_price).toFixed(2)}</span>
                        </div>
                        <div className="item-reason">Reason: {item.reason?.replace('_', ' ')}</div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="totals-section">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>Rs.{totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="total-row small">
                      <span>Tax (included):</span>
                      <span>Rs.{totals.tax.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Refund Total Box */}
                  <div className="refund-total-box">
                    <div className="refund-label">REFUND TOTAL</div>
                    <div className="refund-amount">Rs. {returnResult?.total_refund?.toFixed(2)}</div>
                  </div>

                  {/* Refund Mode */}
                  <div className="refund-mode">
                    <span>Refund Mode:</span>
                    <span className="bold">{refundMode === 'cash' ? 'CASH' : refundMode === 'original' ? 'ORIGINAL METHOD' : 'STORE CREDIT'}</span>
                  </div>

                  {/* Notes */}
                  {notes && (
                    <div className="notes-section">
                      <span className="bold">Notes:</span> {notes}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="receipt-footer">
                    <div className="thank-you">Thank You!</div>
                    <div className="footer-note">Computer Generated Receipt</div>
                  </div>

                  {/* Thermal Print Styles - ALL BOLD */}
                  <style>{`
                    .return-receipt-thermal {
                      width: 280px;
                      padding: 12px;
                      font-family: 'Arial', 'Helvetica', sans-serif;
                      font-size: 12px;
                      line-height: 1.4;
                      background: white;
                      color: black;
                      font-weight: 700;
                    }
                    .return-receipt-thermal .receipt-header {
                      text-align: center;
                      border-bottom: 2px solid black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .receipt-title {
                      font-size: 16px;
                      font-weight: 900;
                      letter-spacing: 1px;
                    }
                    .return-receipt-thermal .store-name {
                      font-size: 12px;
                      font-weight: 800;
                      margin-top: 4px;
                    }
                    .return-receipt-thermal .info-section {
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .info-row {
                      display: flex;
                      justify-content: space-between;
                      font-size: 11px;
                      font-weight: 700;
                      margin-bottom: 2px;
                    }
                    .return-receipt-thermal .bold { font-weight: 900; }
                    .return-receipt-thermal .items-section {
                      border-bottom: 2px solid black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .section-title {
                      font-size: 11px;
                      font-weight: 900;
                      margin-bottom: 4px;
                    }
                    .return-receipt-thermal .items-header {
                      display: flex;
                      font-size: 10px;
                      font-weight: 900;
                      border-bottom: 1px solid black;
                      padding-bottom: 2px;
                      margin-bottom: 6px;
                    }
                    .return-receipt-thermal .items-header .col-item { flex: 1; }
                    .return-receipt-thermal .items-header .col-qty { width: 35px; text-align: center; }
                    .return-receipt-thermal .items-header .col-amt { width: 70px; text-align: right; }
                    .return-receipt-thermal .item-row {
                      margin-bottom: 6px;
                      padding-bottom: 4px;
                      border-bottom: 1px dotted #666;
                    }
                    .return-receipt-thermal .item-row:last-child { border-bottom: none; }
                    .return-receipt-thermal .item-name {
                      font-size: 12px;
                      font-weight: 900;
                      margin-bottom: 2px;
                    }
                    .return-receipt-thermal .item-details {
                      display: flex;
                      font-size: 11px;
                      font-weight: 700;
                    }
                    .return-receipt-thermal .item-details .col-qty { width: 35px; text-align: center; }
                    .return-receipt-thermal .item-details .col-rate { flex: 1; }
                    .return-receipt-thermal .item-details .col-amt { width: 70px; text-align: right; font-weight: 900; }
                    .return-receipt-thermal .item-reason {
                      font-size: 11px;
                      font-weight: 700;
                      color: black;
                      margin-top: 2px;
                    }
                    .return-receipt-thermal .totals-section {
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .total-row {
                      display: flex;
                      justify-content: space-between;
                      font-size: 12px;
                      font-weight: 700;
                    }
                    .return-receipt-thermal .total-row.small { font-size: 11px; font-weight: 700; }
                    .return-receipt-thermal .refund-total-box {
                      border: 2px solid black;
                      padding: 8px;
                      text-align: center;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .refund-label {
                      font-size: 12px;
                      font-weight: 900;
                    }
                    .return-receipt-thermal .refund-amount {
                      font-size: 18px;
                      font-weight: 900;
                    }
                    .return-receipt-thermal .refund-mode {
                      display: flex;
                      justify-content: space-between;
                      font-size: 11px;
                      font-weight: 700;
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .notes-section {
                      font-size: 11px;
                      font-weight: 700;
                      border-bottom: 1px dashed black;
                      padding-bottom: 8px;
                      margin-bottom: 8px;
                    }
                    .return-receipt-thermal .receipt-footer {
                      text-align: center;
                      margin-top: 8px;
                    }
                    .return-receipt-thermal .thank-you {
                      font-size: 14px;
                      font-weight: 900;
                    }
                    .return-receipt-thermal .footer-note {
                      font-size: 10px;
                      font-weight: 700;
                      margin-top: 4px;
                    }
                    @media print {
                      .return-receipt-thermal {
                        width: 70mm !important;
                        padding: 0 !important;
                        padding-left: 3mm !important;
                        padding-right: 2mm !important;
                        padding-top: 2mm !important;
                        padding-bottom: 2mm !important;
                        margin: 0 !important;
                        font-weight: 700 !important;
                      }
                      .return-receipt-thermal * {
                        color: black !important;
                        background: transparent !important;
                        font-weight: 700 !important;
                      }
                      .return-receipt-thermal .receipt-title,
                      .return-receipt-thermal .item-name,
                      .return-receipt-thermal .section-title,
                      .return-receipt-thermal .refund-label,
                      .return-receipt-thermal .refund-amount,
                      .return-receipt-thermal .thank-you {
                        font-weight: 900 !important;
                      }
                      @page {
                        size: 80mm auto;
                        margin: 0;
                      }
                    }
                  `}</style>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print Receipt
              </Button>
              <Button onClick={handleClose} className="bg-emerald-500 hover:bg-emerald-600">
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReturnRefundDialog;
